<template>
    <div class="block-input">
        <label class="label-input" :for="attrs.name">{{ attrs.label }}</label>
        <input
            v-bind="attrs"
            ref="input"
            v-model="displayValue"
            :type="type"
            class="form-control default-input"
            :class="{ error: attrs.error && attrs.error.length }"
            @input="handleInput"
            @focus="isFocused = true"
            @blur="isFocused = false"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

defineOptions({
    inheritAttrs: false,
});

const attrs = useAttrs();

const props = defineProps({
    modelValue: {
        type: [String, Number],
        required: true
    },
    type: {
        type: String,
        default: 'text',
    },
    decimalPlaces: {
        type: Number,
        default: 2,

    }
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string | number): void
}>();

const input = ref(null);
const internalValue = ref(props.modelValue.toString());
const isFocused = ref(false);

const displayValue = computed({
    get() {
        return internalValue.value;
    },
    set(value) {
        internalValue.value = value;
    }
});

const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    let value = target.value;

    value = value.replace(/,/g, '.');
    value = value.replace(/[^\d.-]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
    }

    if (value.includes('.')) {
        const parts = value.split('.');
        if (parts[1].length > props.decimalPlaces) {
            parts[1] = parts[1].slice(0, props.decimalPlaces);
            value = parts.join('.');
        }
    }

    if (attrs.max) {
        const numericValue = parseFloat(value);
        const maxValue = parseFloat(attrs.max as string);
        if (!isNaN(numericValue) && !isNaN(maxValue) && numericValue > maxValue) {
            value = maxValue.toString();
        }
    }

    internalValue.value = value;
    emit('update:modelValue', value);
};

// Следим за изменениями modelValue извне
watch(() => props.modelValue, (newValue) => {
    if (!isFocused.value) {
        let value = newValue.toString();
        if (value.includes('.')) {
            value = value.replace('.', ',');
        }
        internalValue.value = value;
    }
});

defineExpose({ input });
</script>

<style lang="scss" scoped>
.block-input {
    position: relative;
    width: 100%;
    font-family: Roboto;
}
.default-input {
    -webkit-box-shadow: 0 0 0 30px white inset;
    -webkit-text-fill-color: black !important;
    border-radius: 0.1875rem;
    padding: 1.685rem 1rem 0.56rem;
    border: 1px solid var(--main-grey-e-4-e-6-e-7, #e4e6e7);
    background: var(--main-white-ffffff, #fff);
    color: var(--main-black-2-b-2-b-2-b, #2b2b2b);
    font-family: Roboto;
    font-size: 1rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.25rem;
    width: 100%;
    height: 3.5rem;
}
.error {
    border: 1px solid red;
}
.label-input {
    position: absolute;
    top: 0.56rem;
    left: 1rem;
    color: var(--grey-01-aaaaaa, #aaa);
    font-size: 0.6875rem;
    font-style: normal;
    font-weight: 400;
    line-height: 0.875rem;
}
</style>
